import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { fetchUsers, createUser, updateUserRole, toggleUserStatus, deleteUser } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ROLES = ["admin", "moderator", "host", "viewer"];

const ROLE_BADGE = {
  admin:     "bg-[#8A2E2E] text-ivory",
  moderator: "bg-[#2E5A8A] text-ivory",
  host:      "bg-[#2E8A5A] text-ivory",
  viewer:    "bg-[#2A2A2D] text-[#A1A1AA]",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", email: "", password: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const reload = () => fetchUsers().then(setUsers).catch(() => {});
  useEffect(() => { reload(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) return toast.error("Username, email aur password zaroori hain");
    setSaving(true);
    try {
      await createUser(form);
      toast.success(`User "${form.username}" ban gaya`);
      setForm({ username: "", email: "", password: "", role: "viewer" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "User nahi bana");
    } finally {
      setSaving(false);
    }
  };

  const onRoleChange = async (id, role) => {
    try {
      await updateUserRole(id, role);
      toast.success("Role update ho gaya");
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Role update nahi hua");
    }
  };

  const onToggle = async (id) => {
    try {
      const updated = await toggleUserStatus(id);
      toast.success(updated.is_active ? "User active kar diya" : "User disable kar diya");
      setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
    } catch {
      toast.error("Status update nahi hua");
    }
  };

  const onDelete = async (id, username) => {
    if (!window.confirm(`"${username}" ko delete karna chahte hain?`)) return;
    try {
      await deleteUser(id);
      toast.success("User delete ho gaya");
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch {
      toast.error("Delete nahi hua");
    }
  };

  return (
    <div className="space-y-10">
      <div>
        <div className="overline text-[#B79C7B]">Access Control</div>
        <h1 className="font-serif text-4xl text-ivory mt-2">Users & Roles</h1>
        <p className="text-[#A1A1AA] mt-2 text-sm">
          Admin naye users bana sakta hai aur un ko role assign kar sakta hai —
          <span className="text-[#8A2E2E] font-mono mx-1">admin</span>
          <span className="text-[#2E5A8A] font-mono mx-1">moderator</span>
          <span className="text-[#2E8A5A] font-mono mx-1">host</span>
          <span className="text-[#A1A1AA] font-mono mx-1">viewer</span>
        </p>
      </div>

      {/* Add user form */}
      <section className="border border-[#2A2A2D] bg-[#121214] p-8">
        <div className="overline text-[#B79C7B] mb-6">Naya user banana</div>
        <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4">
          <input
            className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory"
            placeholder="Username"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <input
            className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory"
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            className="h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory"
            placeholder="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="bg-[#0A0A0B] border-[#2A2A2D] h-11">
              <SelectValue placeholder="Role chunein" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-[#2A2A2D] text-ivory">
              {ROLES.map((r) => (
                <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="submit"
            disabled={saving}
            className="md:col-span-2 h-11 bg-[#8A2E2E] hover:bg-[#A33B3B] text-sm tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Plus size={16} /> USER BANANA
          </button>
        </form>
      </section>

      {/* Users table */}
      <section>
        <div className="overline text-[#B79C7B] mb-4">Tamam users · {users.length}</div>
        <div className="border border-[#2A2A2D]">
          <div className="grid grid-cols-[1fr_180px_160px_100px_80px_60px] px-5 py-3 bg-[#121214] border-b border-[#2A2A2D] overline text-[#71717A] text-xs">
            <div>User</div>
            <div>Email</div>
            <div>Role</div>
            <div>Status</div>
            <div>Active</div>
            <div></div>
          </div>
          {users.length === 0 && (
            <div className="px-5 py-8 text-center text-[#71717A] text-sm">Koi user nahi mila</div>
          )}
          {users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-[1fr_180px_160px_100px_80px_60px] items-center px-5 py-3 border-b border-[#2A2A2D] text-sm"
            >
              {/* Username */}
              <div className="font-medium text-ivory truncate">{u.username}</div>

              {/* Email */}
              <div className="text-[#A1A1AA] truncate text-xs">{u.email}</div>

              {/* Role dropdown */}
              <Select value={u.role} onValueChange={(v) => onRoleChange(u.id, v)}>
                <SelectTrigger className="bg-[#0A0A0B] border-[#2A2A2D] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-[#2A2A2D] text-ivory">
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} className="capitalize text-xs">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Role badge */}
              <div>
                <span className={`text-[10px] px-2 py-1 font-mono uppercase tracking-wider ${ROLE_BADGE[u.role] || ROLE_BADGE.viewer}`}>
                  {u.role}
                </span>
              </div>

              {/* Active toggle */}
              <button
                onClick={() => onToggle(u.id)}
                className={u.is_active ? "text-[#2E8A5A]" : "text-[#71717A]"}
                title={u.is_active ? "Active — click to disable" : "Disabled — click to enable"}
              >
                {u.is_active ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
              </button>

              {/* Delete */}
              <button
                onClick={() => onDelete(u.id, u.username)}
                className="text-[#71717A] hover:text-[#FF3B30]"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
